<script lang="ts">
  /**
   * BottomTabBar - primary navigation on phones (<768px).
   * The user's 4 configurable tabs (preferences.mobileTabs) plus a fixed More tab.
   * Layout decides when it renders (mobile, logged in, not in Now display mode).
   * Contract: plans/mobile-v2.md §3.2.
   */
  import { Link, page } from '@inertiajs/svelte';
  import { favoriteViews } from '../../lib/stores/favorites';
  import { mobileTabs } from '../../lib/stores/mobileTabs';
  import { nowState } from '../../lib/stores/now';
  import { getTimelinePath } from '../../lib/timelinePath';
  import {
    MOBILE_TAB_LABELS,
    activeMobileTab,
    mobileTabHref,
    type MobileTabId,
  } from '../../lib/mobileTabs';

  // 24px stroke icons (same set as docs/mobile/mockups/shell.js).
  const ICONS: Record<MobileTabId, string> = {
    timeline:
      '<rect x="3" y="4.5" width="18" height="16" rx="3"/><path d="M3 9.5h18M8 2.5v4M16 2.5v4M7.5 13.5h3M7.5 17h6"/>',
    projects:
      '<path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>',
    now: '<circle cx="12" cy="13" r="8"/><path d="M12 9v4l2.5 2.5M9.5 2.5h5"/>',
    habits:
      '<path d="M12 21v-8M12 13c0-4 3-7 8-7 0 4-3 7-8 7zM12 15c0-3-2.5-5.5-7-5.5 0 3 2.5 5.5 7 5.5z"/>',
    calendar:
      '<rect x="3" y="4.5" width="18" height="16" rx="3"/><path d="M3 9.5h18M8 2.5v4M16 2.5v4"/><circle cx="8" cy="14" r=".6"/><circle cx="12" cy="14" r=".6"/><circle cx="16" cy="14" r=".6"/><circle cx="8" cy="17.5" r=".6"/><circle cx="12" cy="17.5" r=".6"/>',
    cycles:
      '<path d="M20 12a8 8 0 0 1-14 5.3M4 12a8 8 0 0 1 14-5.3"/><path d="M18.5 3v4h-4M5.5 21v-4h4"/>',
    boards:
      '<rect x="3" y="4" width="7" height="6" rx="1.5"/><rect x="14" y="14" width="7" height="6" rx="1.5"/><path d="M10 7h4a3 3 0 0 1 3 3v4"/>',
    more: '<circle cx="5.5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="18.5" cy="12" r="1.6"/>',
  };

  let timelinePath = $derived(getTimelinePath($favoriteViews, { mobile: true }));
  let active = $derived(activeMobileTab($page.url, $mobileTabs));
  let nowCount = $derived($nowState.occupied);
  let nowLabel = $derived(`Now, ${nowCount} ${nowCount === 1 ? 'task' : 'tasks'}`);

  let tabs = $derived([
    ...$mobileTabs.map((key) => ({
      id: key as MobileTabId,
      href: mobileTabHref(key, timelinePath),
    })),
    { id: 'more' as MobileTabId, href: '/more' },
  ]);
</script>

<nav class="bottom-tab-bar" aria-label="Primary">
  {#each tabs as tab (tab.id)}
    <Link
      href={tab.href}
      class="tab {active === tab.id ? 'active' : ''}"
      aria-current={active === tab.id ? 'page' : undefined}
      aria-label={tab.id === 'now' && nowCount > 0 ? nowLabel : undefined}
      data-tab={tab.id}
    >
      <span class="tab-icon">
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <!-- eslint-disable-next-line svelte/no-at-html-tags -- static icon markup from the constant above -->
          {@html ICONS[tab.id]}
        </svg>
        {#if tab.id === 'now' && nowCount > 0}
          <span class="badge" aria-hidden="true">{nowCount}</span>
        {/if}
      </span>
      <span class="tab-label">{MOBILE_TAB_LABELS[tab.id]}</span>
    </Link>
  {/each}
</nav>

<style>
  .bottom-tab-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 30;
    display: flex;
    height: calc(var(--tabbar-h) + var(--safe-bottom));
    padding: 0 var(--safe-right) var(--safe-bottom) var(--safe-left);
    background: rgba(255, 255, 255, 0.96);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-top: 1px solid #e5e7eb;
  }

  .bottom-tab-bar :global(.tab) {
    flex: 1;
    min-width: var(--mobile-tap);
    min-height: var(--mobile-tap);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    color: #94a3b8;
    font-size: 11px;
    font-weight: 600;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;
  }

  .bottom-tab-bar :global(.tab.active) {
    color: #2563eb;
  }

  .bottom-tab-bar :global(.tab:focus-visible) {
    outline: 2px solid #2563eb;
    outline-offset: -4px;
    border-radius: 8px;
  }

  .tab-icon {
    position: relative;
    display: flex;
  }

  .tab-icon svg {
    width: 24px;
    height: 24px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .badge {
    position: absolute;
    top: -4px;
    left: calc(100% - 6px);
    min-width: 16px;
    padding: 0 5px;
    border-radius: 999px;
    background: #c2410c;
    color: white;
    font-size: 10px;
    font-weight: 700;
    line-height: 16px;
    text-align: center;
  }

  .tab-label {
    line-height: 1;
  }
</style>
